import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { ArrowLeft, Printer } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCallback } from "react";
import { getDb } from "@/db";
import { cars, events } from "@/db/schema";

const getQRData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		return { event, cars: eventCars };
	});

export const Route = createFileRoute("/events/$eventId/qr")({
	loader: ({ params }) => getQRData({ data: params.eventId }),
	component: QRCodesPage,
});

function QRCodesPage() {
	const { event, cars: carList } = Route.useLoaderData();

	const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

	const handlePrint = useCallback(() => {
		window.print();
	}, []);

	const sortedCars = [...carList].sort((a, b) => a.carNumber - b.carNumber);

	return (
		<div className="min-h-screen bg-background">
			<style>{`
				@media print {
					.no-print { display: none !important; }
					.print-break { page-break-inside: avoid; }
					body { background: white; }
				}
			`}</style>

			{/* Header (hidden in print) */}
			<div className="no-print p-4 sm:p-6">
				<div className="max-w-5xl mx-auto">
					<Link
						to="/events/$eventId"
						params={{ eventId: String(event.id) }}
						className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
					>
						<ArrowLeft size={16} />
						Back to {event.name}
					</Link>

					<div className="flex items-center justify-between mb-6">
						<div>
							<h1 className="text-4xl text-foreground">QR Codes</h1>
							<p className="text-muted-foreground">
								{event.name} &mdash; {carList.length} cars
							</p>
						</div>
						<button
							type="button"
							onClick={handlePrint}
							className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
						>
							<Printer size={14} />
							Print
						</button>
					</div>

					<p className="text-muted-foreground text-sm mb-4">
						Each QR code links to the car's results page. Print and cut to hand
						out to racers, or display on screen for scanning.
					</p>
				</div>
			</div>

			{/* QR code grid */}
			<div className="max-w-5xl mx-auto px-4 sm:px-6 pb-6">
				<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
					{sortedCars.map((car) => {
						const url = `${baseUrl}/events/${event.id}/cars/${car.id}`;
						return (
							<div
								key={car.id}
								className="print-break bg-card rounded-xl p-4 flex flex-col items-center text-center border border-border"
							>
								<QRCodeSVG value={url} size={120} level="M" className="mb-3" />
								<p className="text-foreground font-bold text-sm truncate w-full">
									<span className="text-primary text-xs mr-1">
										#{car.carNumber}
									</span>
									{car.carName}
								</p>
								<p className="text-muted-foreground text-xs truncate w-full">
									{car.driverName}
								</p>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
